import React, { useEffect, useMemo, useRef, useState } from "react";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";

export default function ReturnsPhotosV2({ returnsId, returnsCol = "returns_v2" }) {
  const [folderId, setFolderId] = useState(null);
  const [folderUrl, setFolderUrl] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({});
  const [err, setErr] = useState("");

  const fns = useMemo(() => getFunctions(undefined, "europe-west2"), []);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    if (!returnsId) return;
    const ref = doc(db, returnsCol, returnsId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const s = snap.data()?.storage;
      if (s?.folderId) {
        setFolderId(s.folderId);
        setFolderUrl(s.folderUrl || null);
      }
    });
    return () => unsub();
  }, [returnsId, returnsCol]);

  const refreshImages = useMemo(
    () => async () => {
      if (!folderId) return;
      setLoading(true);
      setErr("");
      try {
        const list = httpsCallable(fns, "spListImages");
        const { data } = await list({ folderItemId: folderId });
        setImages(data.images || []);
      } catch (e) {
        console.error(e);
        setErr(e.message || "Failed to load images");
      } finally {
        setLoading(false);
      }
    },
    [fns, folderId]
  );

  useEffect(() => {
    refreshImages();
  }, [refreshImages]);

  async function getUploadUrl(fileName) {
    const create = httpsCallable(fns, "spCreateUploadSession");
    const { data } = await create({ folderItemId: folderId, fileName });
    return data.uploadUrl;
  }

  async function putWithProgress(uploadUrl, file, key) {
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const pct = Math.round((e.loaded / e.total) * 100);
        setProgress((p) => ({ ...p, [key]: { name: file.name, pct } }));
      };
      xhr.onload = () => {
        setProgress((p) => ({ ...p, [key]: { name: file.name, pct: 100 } }));
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText}`));
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Length", String(file.size));
      xhr.setRequestHeader("Content-Range", `bytes 0-${file.size - 1}/${file.size}`);
      xhr.send(file);
    });
  }

  const onPick = async (files) => {
    if (!folderId) return;
    const arr = Array.from(files || []);
    if (!arr.length) return;

    setBusy(true);
    try {
      for (const f of arr) {
        const key = `${f.name}-${f.size}-${f.lastModified}`;
        const url = await getUploadUrl(f.name);
        await putWithProgress(url, f, key);
      }
      await refreshImages();
    } catch (e) {
      console.error(e);
      alert(e.message || "Upload failed");
    } finally {
      setBusy(false);
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }
  };

  const onDelete = async (itemId) => {
    if (!window.confirm("Delete this image? This cannot be undone.")) return;
    try {
      const del = httpsCallable(fns, "spDeleteDriveItem");
      await del({ itemId });
      await refreshImages();
    } catch (e) {
      console.error(e);
      alert(e.message || "Delete failed");
    }
  };

  const createFolderNow = async () => {
    if (!returnsId) return;
    try {
      setBusy(true);
      const create = httpsCallable(fns, "spEnsureFolder");
      const { data } = await create({ type: "returnsImage", subId: returnsId });
      await updateDoc(doc(db, returnsCol, returnsId), {
        storage: { provider: "sharepoint", folderId: data.folderItemId, folderUrl: data.folderWebUrl },
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Could not create folder");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="photos" className="bg-white border border-gray-200 rounded-xl p-6">
      <h2 className="text-xl font-semibold mb-4">Photos (optional)</h2>

      {!returnsId ? (
        <p className="text-sm text-gray-500">Save a draft first to attach photos.</p>
      ) : !folderId ? (
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-600">Folder not ready yet.</p>
          <button
            type="button"
            onClick={createFolderNow}
            disabled={busy}
            className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          >
            {busy ? "Creating…" : "Create folder"}
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-sm text-gray-700 mr-1">Add photos:</span>

            <button
              type="button"
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
              onClick={() => cameraInputRef.current?.click()}
              disabled={busy}
            >
              📷 Take photo
            </button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => onPick(e.target.files)}
            />

            <button
              type="button"
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
              onClick={() => galleryInputRef.current?.click()}
              disabled={busy}
            >
              🖼️ Upload from gallery
            </button>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onPick(e.target.files)}
            />

            {folderUrl && (
              <a className="ml-auto text-sm underline text-indigo-700" href={folderUrl} target="_blank" rel="noreferrer">
                Open folder
              </a>
            )}

            <button
              type="button"
              onClick={refreshImages}
              disabled={loading || busy}
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          {Object.keys(progress).length > 0 && (
            <div className="mb-3 grid gap-2">
              {Object.entries(progress).map(([k, v]) => (
                <div key={k} className="text-xs">
                  <div className="flex justify-between">
                    <span className="truncate mr-2">{v.name}</span>
                    <span>{v.pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded">
                    <div className="h-2 bg-indigo-600 rounded" style={{ width: `${v.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {err && <p className="text-sm text-red-600 mb-2">{err}</p>}

          {loading ? (
            <p className="text-sm text-gray-500">Loading images…</p>
          ) : images.length === 0 ? (
            <p className="text-sm text-gray-500">No images uploaded.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {images.map((img) => (
                <div key={img.id} className="border rounded overflow-hidden">
                  <a href={img.webUrl} target="_blank" rel="noreferrer">
                    <img
                      src={img.thumbUrl || img.webUrl}
                      alt={img.name}
                      className="w-full h-36 object-cover"
                      loading="lazy"
                    />
                  </a>
                  <div className="p-2 text-xs flex items-center justify-between gap-2">
                    <div className="truncate" title={img.name}>{img.name}</div>
                    <button
                      type="button"
                      onClick={() => onDelete(img.id)}
                      className="text-red-600 hover:underline"
                      disabled={busy}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
